import axios from 'axios';
import {API_BASE_URL} from './authApi';
import {PostReactionType} from './socialFeedApi';

export type CreatorAnalyticsPresetRange = '7d' | '30d' | '90d';
export type CreatorAnalyticsRange = CreatorAnalyticsPresetRange | 'custom';
export type CreatorAnalyticsSelection = CreatorAnalyticsPresetRange | {
  startDate: string;
  endDate: string;
};

export interface CreatorAnalyticsMetric {
  value: number;
  previousValue: number;
  changePercent: number | null;
}

export interface CreatorAnalyticsTrendBucket {
  date: string;
  reactions: number;
  comments: number;
  reposts: number;
  follows: number;
  views: number;
  profileViews: number;
  postViews: number;
  reelViews: number;
  serviceClicks: number;
  postsPublished: number;
  total: number;
}

export interface CreatorAnalyticsContentItem {
  id: string;
  type: 'post' | 'reel';
  title: string;
  createdAt: string;
  score: number;
  reactionCount: number;
  commentCount: number;
  repostCount: number;
  viewCount: number;
  href: string;
  thumbnailUrl: string | null;
  trend: Array<{
    date: string;
    score: number;
  }>;
}

export interface CreatorAnalytics {
  range: CreatorAnalyticsRange;
  generatedAt: string;
  dateRange: {
    startDate: string;
    endDate: string;
    days: number;
    timeZone: string;
  };
  summary: {
    totalEngagements: CreatorAnalyticsMetric;
    profileViews: CreatorAnalyticsMetric;
    serviceClicks: CreatorAnalyticsMetric;
    newFollowers: CreatorAnalyticsMetric;
    postsPublished: CreatorAnalyticsMetric;
    totalFollowers: number;
    totalFollowing: number;
    totalPosts: number;
    totalReelViews: number;
  };
  trend: CreatorAnalyticsTrendBucket[];
  topContent: CreatorAnalyticsContentItem[];
  reactionBreakdown: Array<{
    type: PostReactionType;
    count: number;
  }>;
  engagementByHour: Array<{
    hour: number;
    count: number;
  }>;
  postingInsights: {
    bestTimes: Array<{
      weekday: number;
      startHour: number;
      endHour: number;
      count: number;
    }>;
    activityByWeekday: Array<{
      weekday: number;
      count: number;
    }>;
  };
  followerGrowth: Array<{
    date: string;
    count: number;
  }>;
  recommendations: string[];
}

interface CreatorAnalyticsResponse {
  success: boolean;
  analytics: CreatorAnalytics;
}

interface RecordAnalyticsEventResponse {
  success: boolean;
  recorded: boolean;
}

export interface RecordServiceClickPayload {
  ownerId: string;
  serviceId: string;
  serviceKind?: string;
  action?: 'message' | 'book' | 'learn_more';
}

type AnalyticsErrorResponse = {
  message?: string;
  error?: string | {
    code?: string;
    message?: string;
  };
};

export class AnalyticsApiError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'AnalyticsApiError';
    this.status = status;
    this.code = code;
  }
}

const analyticsClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getAnalyticsError = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as AnalyticsErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? {message: errorData.error, code: undefined}
        : errorData?.error;

    return {
      message: errorData?.message || nestedError?.message || error.message || fallbackMessage,
      code: nestedError?.code,
      status: error.response?.status,
    };
  }

  return {
    message: error instanceof Error ? error.message : fallbackMessage,
    code: undefined,
    status: undefined,
  };
};

export const analyticsApi = {
  async getCreatorAnalytics(
    token: string,
    selection: CreatorAnalyticsSelection,
    timeZone: string,
    signal?: AbortSignal
  ): Promise<CreatorAnalytics> {
    try {
      const params = typeof selection === 'string'
        ? {range: selection, timeZone}
        : {...selection, timeZone};
      const response = await analyticsClient.get<CreatorAnalyticsResponse>('/analytics/creator', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
        signal,
      });

      return response.data.analytics;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      const analyticsError = getAnalyticsError(error, 'Failed to load analytics');
      throw new AnalyticsApiError(analyticsError.message, analyticsError.status, analyticsError.code);
    }
  },

  async recordServiceClick(
    token: string,
    payload: RecordServiceClickPayload,
    signal?: AbortSignal
  ): Promise<RecordAnalyticsEventResponse> {
    try {
      const response = await analyticsClient.post<RecordAnalyticsEventResponse>(
        '/analytics/events',
        {
          eventType: 'service_click',
          entityType: 'service',
          ownerId: payload.ownerId,
          entityId: payload.serviceId,
          metadata: {
            serviceKind: payload.serviceKind,
            action: payload.action,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      const analyticsError = getAnalyticsError(error, 'Failed to record analytics event');
      throw new AnalyticsApiError(analyticsError.message, analyticsError.status, analyticsError.code);
    }
  },
};
